import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent,
  type WheelEvent,
} from "react";
import {
  buildGraphState,
  detailFieldsForCondition,
  visibleGroupsForCondition,
} from "../lib/experiment";
import { clampMapZoom, zoomAtPoint } from "../lib/map-view";
import { placeGroupLabels } from "../lib/group-label-layout";
import type { Condition, Person, Story } from "../types";

type Props = {
  story: Story;
  condition: Condition;
  furthestReadIndex: number;
  onInteraction: (type: string, payload?: Record<string, unknown>) => void;
};

type Point = { x: number; y: number };
type View = Point & { zoom: number };
type Selection = { type: "person" | "relation"; id: string };

const WORLD_WIDTH = 1050;
const WORLD_HEIGHT = 940;
const NODE_WIDTH = 206;

const labels: Record<string, string> = {
  role: "立場",
  status: "現在の状態",
  goal: "目的",
  description: "人物情報",
  attitude: "感情・態度",
  history: "これまでの変化",
};

const defaultPositions = (story: Story): Record<string, Point> =>
  Object.fromEntries(
    story.people.map((person) => [
      person.id,
      { x: (person.x / 100) * WORLD_WIDTH, y: (person.y / 100) * WORLD_HEIGHT },
    ]),
  );

export function RelationMap({ story, condition, furthestReadIndex, onInteraction }: Props) {
  const graph = useMemo(
    () => buildGraphState(story, furthestReadIndex),
    [story, furthestReadIndex],
  );
  const visiblePeople = useMemo(
    () => story.people.filter((person) => graph.people[person.id]?.visible === "true"),
    [graph.people, story.people],
  );
  const visibleRelations = useMemo(
    () => story.relations.filter((relation) => graph.relations[relation.id]?.visible === true),
    [graph.relations, story.relations],
  );

  const [selected, setSelected] = useState<Selection | null>(null);
  const [positions, setPositions] = useState<Record<string, Point>>(() => defaultPositions(story));
  const [nodeSizes, setNodeSizes] = useState<Record<string, { width: number; height: number }>>({});
  const [view, setView] = useState<View>({ x: 25, y: 15, zoom: 0.8 });
  const canvas = useRef<HTMLDivElement>(null);
  const drag = useRef<{
    pointer: number;
    id?: string;
    start: Point;
    origin: Point;
    moved: boolean;
  } | null>(null);
  const wheelLogTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    setPositions(defaultPositions(story));
    setNodeSizes({});
    setSelected(null);
  }, [story.id]);

  useEffect(() => {
    const element = canvas.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (!width || !height) return;
      const zoom = Math.min(width / WORLD_WIDTH, height / WORLD_HEIGHT, 1);
      setView({
        x: (width - WORLD_WIDTH * zoom) / 2,
        y: (height - WORLD_HEIGHT * zoom) / 2,
        zoom,
      });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [story.id]);

  const visiblePersonIds = visiblePeople.map((person) => person.id).join("|");
  useEffect(() => {
    const element = canvas.current;
    if (!element) return;
    const observer = new ResizeObserver((entries) => {
      setNodeSizes((current) => {
        const next = { ...current };
        let changed = false;
        for (const entry of entries) {
          const node = entry.target as HTMLElement;
          const id = node.dataset.personId;
          if (!id) continue;
          const width = node.offsetWidth;
          const height = node.offsetHeight;
          if (width && height && (current[id]?.width !== width || current[id]?.height !== height)) {
            next[id] = { width, height };
            changed = true;
          }
        }
        return changed ? next : current;
      });
    });
    element.querySelectorAll<HTMLElement>("[data-person-id]").forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [condition, visiblePersonIds]);

  useEffect(() => () => window.clearTimeout(wheelLogTimer.current), []);

  const choose = (type: Selection["type"], id: string) => {
    setSelected({ type, id });
    onInteraction(type === "person" ? "map_person_selected" : "map_relation_selected", { id });
  };

  const fit = (reset = false) => {
    const nextPositions = reset ? defaultPositions(story) : positions;
    const targets = visiblePeople.length ? visiblePeople : story.people;
    const boxes = targets.map((person) => {
      const point = nextPositions[person.id];
      const size = nodeSizes[person.id] ?? { width: NODE_WIDTH, height: 84 };
      return { ...point, ...size };
    });
    const bounds = canvas.current?.getBoundingClientRect();
    if (!bounds || boxes.length === 0) return;
    const minX = Math.min(...boxes.map((box) => box.x - box.width / 2)) - 55;
    const minY = Math.min(...boxes.map((box) => box.y - box.height / 2)) - 55;
    const maxX = Math.max(...boxes.map((box) => box.x + box.width / 2)) + 55;
    const maxY = Math.max(...boxes.map((box) => box.y + box.height / 2)) + 55;
    const width = maxX - minX;
    const height = maxY - minY;
    const zoom = clampMapZoom(Math.min(bounds.width / width, bounds.height / height, 1));
    setView({
      x: (bounds.width - width * zoom) / 2 - minX * zoom,
      y: (bounds.height - height * zoom) / 2 - minY * zoom,
      zoom,
    });
    if (reset) setPositions(nextPositions);
    onInteraction(reset ? "map_layout_reset" : "map_fit", { zoom });
  };

  const zoomBy = (factor: number) => {
    const bounds = canvas.current?.getBoundingClientRect();
    if (!bounds) return;
    setView((current) =>
      zoomAtPoint(current, current.zoom * factor, {
        x: bounds.width / 2,
        y: bounds.height / 2,
      }),
    );
    onInteraction("map_zoomed", { source: "button", factor });
  };

  const handleWheel = (event: WheelEvent<HTMLDivElement>) => {
    event.preventDefault();
    const bounds = canvas.current?.getBoundingClientRect();
    if (!bounds) return;
    const point = { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
    const delta = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
    const factor = Math.exp(-delta * 0.0015);
    setView((current) => zoomAtPoint(current, current.zoom * factor, point));
    window.clearTimeout(wheelLogTimer.current);
    wheelLogTimer.current = window.setTimeout(() => {
      onInteraction("map_zoomed", { source: "wheel" });
    }, 350);
  };

  const startDrag = (event: PointerEvent<HTMLElement>, id?: string) => {
    if (event.button !== 0 || drag.current) return;
    event.stopPropagation();
    if (id) choose("person", id);
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = {
      pointer: event.pointerId,
      id,
      start: { x: event.clientX, y: event.clientY },
      origin: id ? positions[id] : view,
      moved: false,
    };
  };

  const move = (event: PointerEvent<HTMLElement>) => {
    const current = drag.current;
    if (!current || current.pointer !== event.pointerId) return;
    const dx = event.clientX - current.start.x;
    const dy = event.clientY - current.start.y;
    if (Math.abs(dx) + Math.abs(dy) > 3) current.moved = true;
    if (current.id) {
      setPositions((items) => ({
        ...items,
        [current.id!]: {
          x: current.origin.x + dx / view.zoom,
          y: current.origin.y + dy / view.zoom,
        },
      }));
    } else {
      setView((item) => ({ ...item, x: current.origin.x + dx, y: current.origin.y + dy }));
    }
  };

  const finishDrag = () => {
    const current = drag.current;
    if (current?.moved) {
      onInteraction(current.id ? "map_person_dragged" : "map_panned", current.id ? { id: current.id } : {});
    }
    drag.current = null;
  };

  const people = visiblePeople.map((person) => ({ ...person, ...positions[person.id] }));
  const personById = new Map(people.map((person) => [person.id, person]));
  const groupLayouts = visibleGroupsForCondition(story, condition, furthestReadIndex)
    .map((group) => {
      const members = group.members
        .map((memberId) => personById.get(memberId))
        .filter((person): person is Person & Point => Boolean(person));
      if (members.length < 2) return null;
      const left = Math.min(...members.map((person) => person.x - (nodeSizes[person.id]?.width ?? NODE_WIDTH) / 2)) - 30;
      const top = Math.min(...members.map((person) => person.y - (nodeSizes[person.id]?.height ?? 84) / 2)) - 35;
      const right = Math.max(...members.map((person) => person.x + (nodeSizes[person.id]?.width ?? NODE_WIDTH) / 2)) + 30;
      const bottom = Math.max(...members.map((person) => person.y + (nodeSizes[person.id]?.height ?? 84) / 2)) + 35;
      return { ...group, left, top, right, bottom };
    })
    .filter((group): group is NonNullable<typeof group> => Boolean(group));
  const groupLabelObstacles = people.map((person) => {
    const size = nodeSizes[person.id] ?? { width: NODE_WIDTH, height: 180 };
    return {
      left: person.x - size.width / 2,
      top: person.y - size.height / 2,
      right: person.x + size.width / 2,
      bottom: person.y + size.height / 2,
    };
  });
  const groupLabelLines = visibleRelations.flatMap((relation) => {
    const from = personById.get(relation.from);
    const to = personById.get(relation.to);
    return from && to ? [{ x1: from.x, y1: from.y, x2: to.x, y2: to.y }] : [];
  });
  const diagramCenter = people.length ? {
    x: (Math.min(...people.map((person) => person.x)) + Math.max(...people.map((person) => person.x))) / 2,
    y: (Math.min(...people.map((person) => person.y)) + Math.max(...people.map((person) => person.y))) / 2,
  } : { x: WORLD_WIDTH / 2, y: WORLD_HEIGHT / 2 };
  const groupLabels = placeGroupLabels(
    groupLayouts,
    WORLD_WIDTH,
    WORLD_HEIGHT,
    groupLabelObstacles,
    groupLabelLines,
    diagramCenter,
  );

  const selectedBase = selected?.type === "person"
    ? story.people.find((person) => person.id === selected.id)
    : story.relations.find((relation) => relation.id === selected?.id);
  const selectedDetails = selected?.type === "person"
    ? graph.people[selected.id]
    : selected?.type === "relation"
      ? graph.relations[selected.id]
      : undefined;
  const details = selectedDetails ? detailFieldsForCondition(condition, selectedDetails) : {};

  return (
    <div className="map-layout relation-lab-map review-relation-schema">
      <section className="relation-canvas" aria-label="人物相関図">
        <div className="map-toolbar">
          <button type="button" aria-label="縮小" onClick={() => zoomBy(1 / 1.2)}>−</button>
          <output aria-label="拡大倍率">{Math.round(view.zoom * 100)}%</output>
          <button type="button" aria-label="拡大" onClick={() => zoomBy(1.2)}>＋</button>
          <button type="button" onClick={() => fit()}>全体を表示</button>
          <button type="button" onClick={() => fit(true)}>配置を戻す</button>
        </div>
        <p className="map-help">
          人物をドラッグして位置を調整。余白のドラッグで図全体を移動。図の上でスクロールすると拡大・縮小できます。
        </p>
        <div
          className="map-viewport"
          ref={canvas}
          onWheel={handleWheel}
          onPointerDown={(event) => startDrag(event)}
          onPointerMove={move}
          onPointerUp={finishDrag}
          onPointerCancel={finishDrag}
          onLostPointerCapture={finishDrag}
        >
          <div
            className="map-world"
            style={{
              width: WORLD_WIDTH,
              height: WORLD_HEIGHT,
              transform: `translate(${view.x}px, ${view.y}px) scale(${view.zoom})`,
            }}
          >
            {groupLayouts.map((group) => (
              <div
                key={group.id}
                className="group-hull"
                style={{
                  left: group.left,
                  top: group.top,
                  width: group.right - group.left,
                  height: group.bottom - group.top,
                  borderColor: group.color,
                  background: `${group.color}0D`,
                }}
              />
            ))}
            <svg className="relation-layer" width={WORLD_WIDTH} height={WORLD_HEIGHT} aria-label="人物間の関係線">
              {visibleRelations.map((relation) => {
                const from = personById.get(relation.from);
                const to = personById.get(relation.to);
                if (!from || !to) return null;
                const length = Math.hypot(to.x - from.x, to.y - from.y) || 1;
                const ux = (to.x - from.x) / length;
                const uy = (to.y - from.y) / length;
                const boundary = (person: Person & Point) => {
                  const size = nodeSizes[person.id] ?? { width: NODE_WIDTH, height: 84 };
                  return Math.min(
                    Math.abs(ux) < 0.001 ? Infinity : size.width / 2 / Math.abs(ux),
                    Math.abs(uy) < 0.001 ? Infinity : size.height / 2 / Math.abs(uy),
                  ) + 8;
                };
                const start = Math.min(boundary(from), length / 2);
                const end = Math.min(boundary(to), length / 2);
                const x1 = from.x + ux * start;
                const y1 = from.y + uy * start;
                const x2 = to.x - ux * end;
                const y2 = to.y - uy * end;
                const relationDetails = graph.relations[relation.id];
                const relationText = String(relationDetails?.label ?? "関係");
                const attitudeText = condition === "H" ? String(relationDetails?.attitude ?? "") : "";
                const kindWidth = Math.max(72, relationText.length * 11 + 18);
                const attitudeWidth = Math.max(56, attitudeText.length * 10 + 18);
                const selectedClass = selected?.type === "relation" && selected.id === relation.id ? " is-selected" : "";
                return (
                  <g key={relation.id} className={`relation-group${selectedClass}`}>
                    <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={relation.color} strokeWidth={2.6} />
                    <line
                      className="relation-hit-line"
                      x1={x1}
                      y1={y1}
                      x2={x2}
                      y2={y2}
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={() => choose("relation", relation.id)}
                    />
                    <g
                      className="relation-label-group"
                      transform={`translate(${(from.x + to.x) / 2}, ${(from.y + to.y) / 2})`}
                      role="button"
                      tabIndex={0}
                      aria-label={`${from.name}と${to.name}の関係：${relationText}`}
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={() => choose("relation", relation.id)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") choose("relation", relation.id);
                      }}
                    >
                      <rect className="relation-kind-label" x={-kindWidth / 2} y={attitudeText ? -18 : -13} width={kindWidth} height={26} rx={2} />
                      <text className="kind" textAnchor="middle" y={attitudeText ? -1 : 4}>{relationText}</text>
                      {attitudeText && (
                        <>
                          <rect className="emotion-tag emotion-tone-neutral" x={-attitudeWidth / 2} y={11} width={attitudeWidth} height={22} rx={7} />
                          <text className="emotion" textAnchor="middle" y={26}>{attitudeText}</text>
                        </>
                      )}
                    </g>
                  </g>
                );
              })}
            </svg>

            {groupLabels.map((group) => (
              <span
                key={`${group.id}-label`}
                className="group-label"
                style={{
                  left: group.x,
                  top: group.y,
                  width: group.width,
                  height: group.height,
                  background: group.color,
                }}
              >
                {group.name}
              </span>
            ))}

            {people.map((person) => {
              const cardDetails = detailFieldsForCondition(condition, graph.people[person.id]);
              return (
                <article
                  key={person.id}
                  data-person-id={person.id}
                  className={`person-node cards ${selected?.type === "person" && selected.id === person.id ? "is-selected" : ""}`}
                  style={{
                    left: person.x,
                    top: person.y,
                    "--person-color": person.color,
                  } as CSSProperties}
                  tabIndex={0}
                  aria-label={`${person.name}。ドラッグまたは矢印キーで移動`}
                  onPointerDown={(event) => startDrag(event, person.id)}
                  onFocus={() => setSelected({ type: "person", id: person.id })}
                  onKeyDown={(event) => {
                    const deltas: Record<string, Point> = {
                      ArrowLeft: { x: -20, y: 0 },
                      ArrowRight: { x: 20, y: 0 },
                      ArrowUp: { x: 0, y: -20 },
                      ArrowDown: { x: 0, y: 20 },
                    };
                    const delta = deltas[event.key];
                    if (!delta) return;
                    event.preventDefault();
                    setPositions((current) => ({
                      ...current,
                      [person.id]: {
                        x: current[person.id].x + delta.x,
                        y: current[person.id].y + delta.y,
                      },
                    }));
                    onInteraction("map_person_moved_by_keyboard", { id: person.id, key: event.key });
                  }}
                >
                  <div className="node-head">
                    <span className="pictogram kind-person" aria-hidden="true"><i>◆</i></span>
                    <div className="node-name"><b>{person.name}</b></div>
                    <span className="drag-handle" aria-hidden="true">⠿</span>
                  </div>
                  {Object.keys(cardDetails).length > 0 && (
                    <div className="node-details person-information">
                      {Object.entries(cardDetails).map(([key, value]) => (
                        <section key={key} className="information-group" data-category={key}>
                          <h3>{labels[key] ?? key}</h3>
                          <div className="information-values"><p>{String(value)}</p></div>
                        </section>
                      ))}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <aside className="detail-panel" aria-live="polite">
        {selected && selectedBase && selectedDetails ? (
          <>
            <p className="eyebrow">SELECTED {selected.type.toUpperCase()}</p>
            <h3>
              {selected.type === "person"
                ? "name" in selectedBase
                  ? selectedBase.name
                  : "人物"
                : String(selectedDetails.label ?? "人物間の関係")}
            </h3>
            {condition === "L" && (
              <p className="muted-copy">この表示では、人物名と基本的な関係だけを確認できます。</p>
            )}
            {Object.entries(details).map(([key, value]) => (
              <div className="detail-row" key={key}>
                <dt>{labels[key] ?? key}</dt>
                <dd>{String(value)}</dd>
              </div>
            ))}
          </>
        ) : (
          <div className="detail-empty">
            <span aria-hidden="true">↖</span>
            <p>人物または関係を選択してください</p>
          </div>
        )}
      </aside>
    </div>
  );
}
